import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import { StatsContainer } from '../../molecules/StatsContainer'
import {
  ChartContainer,
  ChartGrid,
  ChartSection,
  ChartTitle,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import {
  formatPricePerSqm,
  formatPercentage,
  getChartColors,
  calculateKPIs,
  generateInsights,
  processTimeSeriesData,
  processPropertyCategoryData,
  processRegionalData,
  formatQuarter,
} from './NewVsResaleChart.utils'
import {
  NewVsResaleChartProps,
  NewVsResaleChartRef,
  ChartViewType,
} from './NewVsResaleChart.types'
import { StatCardVariant } from '../../atoms/StatCard/StatCard.types'

// Default translation function fallback
const defaultT = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

// Helper to translate property category names
const translateCategory = (
  category: string,
  t: (key: string) => string
): string => {
  const key = category.toLowerCase()
  if (key === 'apartment') return t('charts.categories.apartment')
  if (key === 'house') return t('charts.categories.house')
  if (key === 'all types' || key === 'all') return t('charts.labels.allTypes')
  return category
}

export const NewVsResaleChart = forwardRef<
  NewVsResaleChartRef,
  NewVsResaleChartProps
>(
  (
    {
      data = [],
      locationValue = '',
      loading = false,
      error = null,
      isRental = false,
      showKPIs = true,
      showInsights = true,
      showControls = true,
      accentColor = '#00ade6',
      t = defaultT,
    },
    ref
  ) => {
    // Build translatable VIEW_TYPES and PRICE_TYPES
    const VIEW_TYPES: ChartViewType[] = [
      { type: 'composition', label: t('charts.viewTypes.stacked') },
      { type: 'percentage', label: t('charts.viewTypes.percentage') },
      { type: 'absolute', label: t('charts.viewTypes.sideBySide') },
    ]

    const PRICE_TYPES = [
      { type: 'median' as const, label: t('charts.priceTypes.median') },
      { type: 'average' as const, label: t('charts.priceTypes.average') },
    ]
    const [currentView, setCurrentView] =
      useState<ChartViewType['type']>('percentage')
    const [priceType, setPriceType] = useState<'median' | 'average'>('median')
    const compositionChartRef = useRef<HTMLDivElement>(null)
    const premiumChartRef = useRef<HTMLDivElement>(null)
    const regionalChartRef = useRef<HTMLDivElement>(null)

    const compositionInstance = useRef<echarts.ECharts | null>(null)
    const premiumInstance = useRef<echarts.ECharts | null>(null)
    const regionalInstance = useRef<echarts.ECharts | null>(null)
    const isPrintingRef = useRef(false)

    useEffect(() => {
      if (!loading && !error && timeSeries.length > 0) {
        createCharts()
      }

      return () => {
        compositionInstance.current?.dispose()
        premiumInstance.current?.dispose()
        regionalInstance.current?.dispose()
      }
    }, [currentView, loading, error])

    // Process data
    const timeSeries = processTimeSeriesData(data)
    const categories = processPropertyCategoryData(data, priceType)
    const regional = processRegionalData(data, locationValue)

    // Calculate and set KPIs
    const kpis = calculateKPIs(data, priceType)

    // Get translated price type label
    const priceTypeLabel = priceType === 'median'
      ? t('charts.priceTypes.median')
      : t('charts.priceTypes.average')
    const priceUnitLabel = isRental
      ? t('charts.labels.rentPerM2Chf').replace(' (CHF)', '')
      : t('charts.labels.pricePerM2Chf').replace(' (CHF)', '')

    const kpiData = [
      {
        id: 'new-share',
        label: t('charts.newVsResale.kpis.newShare'),
        value: formatPercentage(kpis.newPercentage),
        variant: 'default' as StatCardVariant,
      },
      {
        id: 'avg-price-new',
        label: t('charts.newVsResale.kpis.priceNew', {
          priceType: priceTypeLabel,
          priceUnit: priceUnitLabel
        }),
        value: formatPricePerSqm(kpis.avgPriceSqmNew),
        variant: 'default' as StatCardVariant,
      },
      {
        id: 'avg-price-existing',
        label: t('charts.newVsResale.kpis.priceExisting', {
          priceType: priceTypeLabel,
          priceUnit: priceUnitLabel
        }),
        value: formatPricePerSqm(kpis.avgPriceSqmExisting),
        variant: 'default' as StatCardVariant,
      },
      {
        id: 'premium',
        label: t('charts.newVsResale.kpis.premium'),
        value: `${kpis.premium >= 0 ? '+' : ''}${kpis.premium.toFixed(1)}%`,
        variant: 'default' as StatCardVariant,
      },
    ]

    // Generate insights
    const insights = generateInsights(
      timeSeries,
      categories,
      regional,
      kpis,
      priceType,
      t
    )

    // Expose methods via ref for PDF export
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (compositionInstance.current) {
          compositionInstance.current.resize()
          return compositionInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportPremiumChart: () => {
        if (premiumInstance.current) {
          premiumInstance.current.resize()
          return premiumInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportRegionalChart: () => {
        if (regionalInstance.current) {
          regionalInstance.current.resize()
          return regionalInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getKPIs: () => kpiData,
      getInsights: () => insights,
    }))

    const createCharts = () => {
      if (
        !compositionChartRef.current ||
        !premiumChartRef.current ||
        !regionalChartRef.current
      )
        return

      createCompositionChart()
      createPremiumChart()
      createRegionalChart()
    }

    const createCompositionChart = () => {
      if (!compositionChartRef.current) return

      if (compositionInstance.current) {
        compositionInstance.current.dispose()
      }

      compositionInstance.current = echarts.init(
        compositionChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      if (isPrintingRef.current) {
        setTimeout(() => {
          compositionInstance.current?.resize()
        }, 50)
      }

      const colors = getChartColors()
      const option = getCompositionChartOption(colors)
      compositionInstance.current.setOption(option)
    }

    const getCompositionChartOption = (colors: any) => {
      const xAxisData = timeSeries.map((d) => formatQuarter(d.date))

      switch (currentView) {
        case 'percentage':
          return createPercentageOption(xAxisData, colors)
        case 'absolute':
          return createAbsoluteOption(xAxisData, colors)
        default:
          return createStackedOption(xAxisData, colors)
      }
    }

    // Translated legend labels
    const newConstructionLabel = t('charts.newVsResale.legends.newConstruction')
    const existingPropertiesLabel = t('charts.newVsResale.legends.existingProperties')
    const numberOfListingsLabel = t('charts.labels.numberOfListings')
    const percentageLabel = t('charts.labels.percentage')
    const totalLabel = t('charts.newVsResale.tooltips.total')

    const createStackedOption = (xAxisData: string[], colors: any) => {
      return {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            const total = params[0].value + params[1].value
            return `${params[0].axisValue}<br/>
                  ${newConstructionLabel}: ${params[0].value.toLocaleString()} (${((params[0].value / total) * 100).toFixed(1)}%)<br/>
                  ${existingPropertiesLabel}: ${params[1].value.toLocaleString()} (${((params[1].value / total) * 100).toFixed(1)}%)<br/>
                  ${totalLabel}: ${total.toLocaleString()}`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
          },
        },
        yAxis: {
          type: 'value',
          name: numberOfListingsLabel,
          nameTextStyle: {
            color: '#6c757d',
            padding: [0, 0, 0, 40],
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'line',
            stack: 'Total',
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${colors.new}33` },
                { offset: 1, color: `${colors.new}0D` },
              ]),
            },
            lineStyle: {
              color: colors.new,
              width: 3,
            },
            itemStyle: {
              color: colors.new,
            },
            emphasis: {
              focus: 'series',
            },
            data: timeSeries.map((d) => d.newCount),
          },
          {
            name: existingPropertiesLabel,
            type: 'line',
            stack: 'Total',
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${colors.existing}33` },
                { offset: 1, color: `${colors.existing}0D` },
              ]),
            },
            lineStyle: {
              color: colors.existing,
              width: 3,
            },
            itemStyle: {
              color: colors.existing,
            },
            emphasis: {
              focus: 'series',
            },
            data: timeSeries.map((d) => d.existingCount),
          },
        ],
      }
    }

    const createPercentageOption = (xAxisData: string[], colors: any) => {
      const percentageData = timeSeries.map((d) => {
        const total = d.newCount + d.existingCount
        return {
          new: total > 0 ? (d.newCount / total) * 100 : 0,
          existing: total > 0 ? (d.existingCount / total) * 100 : 0,
        }
      })

      return {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            return `${params[0].axisValue}<br/>
                  ${newConstructionLabel}: ${params[0].value.toFixed(1)}%<br/>
                  ${existingPropertiesLabel}: ${params[1].value.toFixed(1)}%`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
          },
        },
        yAxis: {
          type: 'value',
          name: percentageLabel,
          nameTextStyle: {
            color: '#6c757d',
            padding: [0, 0, 0, 40],
          },
          min: 0,
          max: 100,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
            formatter: '{value}%',
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'line',
            stack: 'Total',
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${colors.new}33` },
                { offset: 1, color: `${colors.new}0D` },
              ]),
            },
            lineStyle: {
              color: colors.new,
              width: 3,
            },
            itemStyle: {
              color: colors.new,
            },
            emphasis: {
              focus: 'series',
            },
            data: percentageData.map((d) => d.new),
          },
          {
            name: existingPropertiesLabel,
            type: 'line',
            stack: 'Total',
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${colors.existing}33` },
                { offset: 1, color: `${colors.existing}0D` },
              ]),
            },
            lineStyle: {
              color: colors.existing,
              width: 3,
            },
            itemStyle: {
              color: colors.existing,
            },
            emphasis: {
              focus: 'series',
            },
            data: percentageData.map((d) => d.existing),
          },
        ],
      }
    }

    const createAbsoluteOption = (xAxisData: string[], colors: any) => {
      return {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            return `${params[0].axisValue}<br/>
                  ${newConstructionLabel}: ${params[0].value.toLocaleString()}<br/>
                  ${existingPropertiesLabel}: ${params[1].value.toLocaleString()}`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: xAxisData,
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
          },
        },
        yAxis: {
          type: 'value',
          name: numberOfListingsLabel,
          nameTextStyle: {
            color: '#6c757d',
            padding: [0, 0, 0, 40],
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              color: colors.new,
              width: 3,
            },
            itemStyle: {
              color: colors.new,
            },
            emphasis: {
              focus: 'series',
              lineStyle: { width: 4 },
            },
            data: timeSeries.map((d) => d.newCount),
          },
          {
            name: existingPropertiesLabel,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              color: colors.existing,
              width: 3,
            },
            itemStyle: {
              color: colors.existing,
            },
            emphasis: {
              focus: 'series',
              lineStyle: { width: 4 },
            },
            data: timeSeries.map((d) => d.existingCount),
          },
        ],
      }
    }

    // Get price per m² label for premium chart
    const pricePerM2Label = isRental
      ? t('charts.labels.rentPerM2Chf')
      : t('charts.labels.pricePerM2Chf')
    const premiumLabel = t('charts.newVsResale.tooltips.premiumLabel')
    const newLabel = t('charts.newVsResale.tooltips.new')
    const existingLabel = t('charts.newVsResale.tooltips.existing')

    const createPremiumChart = () => {
      if (!premiumChartRef.current) return

      if (premiumInstance.current) {
        premiumInstance.current.dispose()
      }

      premiumInstance.current = echarts.init(
        premiumChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      if (isPrintingRef.current) {
        setTimeout(() => {
          premiumInstance.current?.resize()
        }, 50)
      }

      const colors = getChartColors()
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const category = params[0].axisValue
            const newPriceSqm = params[0].value
            const existingPriceSqm = params[1].value
            const premium = (
              ((newPriceSqm - existingPriceSqm) / existingPriceSqm) *
              100
            ).toFixed(1)
            return `${category}<br/>
                  ${newLabel}: ${formatPricePerSqm(newPriceSqm)}<br/>
                  ${existingLabel}: ${formatPricePerSqm(existingPriceSqm)}<br/>
                  ${premiumLabel}: ${premium}%`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: categories.map((d) => translateCategory(d.category, t)),
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
            interval: 0,
          },
        },
        yAxis: {
          type: 'value',
          name: pricePerM2Label,
          nameTextStyle: {
            color: '#6c757d',
            padding: [0, 0, 0, 40],
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
            formatter: (value: number) => value.toLocaleString(),
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'bar',
            barWidth: '30%',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: colors.new },
                { offset: 1, color: colors.newLight },
              ]),
              borderRadius: [4, 4, 0, 0],
            },
            emphasis: {
              itemStyle: {
                color: '#1e40af',
              },
            },
            data: categories.map((d) => d.newPriceSqm),
          },
          {
            name: existingPropertiesLabel,
            type: 'bar',
            barWidth: '30%',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: colors.existing },
                { offset: 1, color: colors.existingLight },
              ]),
              borderRadius: [4, 4, 0, 0],
            },
            emphasis: {
              itemStyle: {
                color: '#059669',
              },
            },
            data: categories.map((d) => d.existingPriceSqm),
          },
        ],
      }

      premiumInstance.current.setOption(option)
    }

    const createRegionalChart = () => {
      if (!regionalChartRef.current) return

      if (regionalInstance.current) {
        regionalInstance.current.dispose()
      }

      regionalInstance.current = echarts.init(
        regionalChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      if (isPrintingRef.current) {
        setTimeout(() => {
          regionalInstance.current?.resize()
        }, 50)
      }

      const colors = getChartColors()
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const name = params[0].axisValue
            const newCount = params[0].value
            const existingCount = params[1].value
            const total = newCount + existingCount
            return `${name}<br/>
                  ${newLabel}: ${newCount.toLocaleString()} (${((newCount / total) * 100).toFixed(1)}%)<br/>
                  ${existingLabel}: ${existingCount.toLocaleString()} (${((existingCount / total) * 100).toFixed(1)}%)<br/>
                  ${totalLabel}: ${total.toLocaleString()}`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          name: numberOfListingsLabel,
          nameTextStyle: {
            color: '#6c757d',
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        yAxis: {
          type: 'category',
          data: regional.map((d) => d.name).reverse(),
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          axisLabel: {
            color: '#6c757d',
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'bar',
            stack: 'total',
            barWidth: '60%',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: colors.newLight },
                { offset: 1, color: colors.new },
              ]),
              borderRadius: [0, 4, 4, 0],
            },
            emphasis: {
              itemStyle: {
                color: '#1e40af',
              },
            },
            data: regional.map((d) => d.newCount).reverse(),
          },
          {
            name: existingPropertiesLabel,
            type: 'bar',
            stack: 'total',
            barWidth: '60%',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: colors.existingLight },
                { offset: 1, color: colors.existing },
              ]),
              borderRadius: [0, 4, 4, 0],
            },
            emphasis: {
              itemStyle: {
                color: '#059669',
              },
            },
            data: regional.map((d) => d.existingCount).reverse(),
          },
        ],
      }

      regionalInstance.current.setOption(option)
    }

    // Handle window resize and print events
    useEffect(() => {
      const handleResize = () => {
        compositionInstance.current?.resize()
        premiumInstance.current?.resize()
        regionalInstance.current?.resize()
      }

      const handleBeforePrint = () => {
        isPrintingRef.current = true
        const resizeCharts = () => {
          if (compositionInstance.current) {
            compositionInstance.current.resize()
          }
          if (premiumInstance.current) {
            premiumInstance.current.resize()
          }
          if (regionalInstance.current) {
            regionalInstance.current.resize()
          }
        }
        resizeCharts()
        setTimeout(resizeCharts, 50)
        setTimeout(resizeCharts, 150)
        setTimeout(resizeCharts, 300)
      }

      const handleAfterPrint = () => {
        isPrintingRef.current = false
        setTimeout(() => {
          compositionInstance.current?.resize()
          premiumInstance.current?.resize()
          regionalInstance.current?.resize()
        }, 100)
      }

      const mediaQueryList = window.matchMedia('print')
      const handleMediaQueryChange = (e: any) => {
        if (e.matches) {
          handleBeforePrint()
        } else {
          handleAfterPrint()
        }
      }

      if (mediaQueryList.matches) {
        handleBeforePrint()
      }

      window.addEventListener('resize', handleResize)
      window.addEventListener('beforeprint', handleBeforePrint)
      window.addEventListener('afterprint', handleAfterPrint)

      if (mediaQueryList.addEventListener) {
        mediaQueryList.addEventListener('change', handleMediaQueryChange)
      } else {
        mediaQueryList.addListener(handleMediaQueryChange)
      }

      return () => {
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('beforeprint', handleBeforePrint)
        window.removeEventListener('afterprint', handleAfterPrint)

        if (mediaQueryList.removeEventListener) {
          mediaQueryList.removeEventListener('change', handleMediaQueryChange)
        } else {
          mediaQueryList.removeListener(handleMediaQueryChange)
        }
      }
    }, [])

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            {t('charts.states.loadingNewVsResale')}
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    if (data.length === 0) {
      return (
        <NotAvailable
          title={t('charts.empty.title')}
          description={t('charts.empty.newVsResale')}
        />
      )
    }

    return (
      <PrintWrapper>
        <Box marginBottom="24px">
          <Box display="flex" justifyContent="flex-end">
            <ButtonGroup>
              {PRICE_TYPES.map((pt) => (
                <Button
                  key={pt.type}
                  variant={priceType === pt.type ? 'primary' : 'secondary'}
                  size="small"
                  onClick={() => setPriceType(pt.type)}
                >
                  {pt.label}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
        </Box>
        {showKPIs && kpiData.length > 0 && (
          <Box marginBottom="30px">
            <StatsContainer fullWidth stats={kpiData} />
          </Box>
        )}

        <Box marginBottom="30px">
          <ChartSection>
            {showControls && (
              <Box marginBottom="24px">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap="16px"
                >
                  <ChartTitle>
                    {t('charts.newVsResale.titles.composition')}
                  </ChartTitle>
                  <ButtonGroup>
                    {VIEW_TYPES.map((view) => (
                      <Button
                        key={view.type}
                        variant={
                          currentView === view.type ? 'primary' : 'secondary'
                        }
                        size="small"
                        onClick={() => setCurrentView(view.type)}
                      >
                        {view.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              </Box>
            )}
            <ChartContainer height={500} ref={compositionChartRef} />
          </ChartSection>
        </Box>

        <ChartGrid>
          <ChartSection
            title={t('charts.newVsResale.titles.premium', {
              priceType: isRental ? t('charts.labels.rent') : t('charts.labels.price')
            })}
          >
            <ChartContainer height={400} ref={premiumChartRef} />
          </ChartSection>
          <ChartSection title={t('charts.newVsResale.titles.activity', { location: locationValue })}>
            <ChartContainer height={400} ref={regionalChartRef} />
          </ChartSection>
        </ChartGrid>

        {showInsights && insights.length > 0 && (
          <Box marginTop="30px">
            <ChartTitle style={{ marginBottom: '24px' }}>
              {t('charts.insights.keyInsights')}
            </ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="24px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 280px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                    borderLeft: `4px solid ${accentColor}`,
                    minWidth: 0,
                  }}
                >
                  <Text
                    variant="h4"
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

NewVsResaleChart.displayName = 'NewVsResaleChart'
